import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { ShareBar } from "../components/ShareBar";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const { lang, copy } = useLanguage();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios.get(`${API}/posts/${slug}?lang=${lang}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug, lang]);

  return (
    <main className="pt-28 pb-24 sm:pt-36" data-testid="blog-post-page">
      {post && (
        <Seo
          title={post.title}
          description={post.excerpt}
          image={post.image}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.date,
            inLanguage: lang,
            author: { "@type": "Person", name: "Giada", jobTitle: "Coach de nutrición y bienestar" },
            publisher: { "@type": "Organization", name: "Gusto y Ciencia" },
          }}
        />
      )}
      <div className="max-w-3xl mx-auto px-5">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-clay hover:text-clay-dark transition-colors mb-10">
          <ArrowLeft size={15} /> {copy.blog.back}
        </Link>
        {notFound && <p className="text-muted" data-testid="blog-not-found">404</p>}
        {post && (
          <Reveal>
            <p className="text-[11px] font-mono uppercase tracking-widest text-clay">{post.category} · {post.read_time} · {post.date}</p>
            <h1 className="mt-3 font-serif font-semibold text-3xl sm:text-5xl text-ink tracking-tight text-balance" data-testid="blog-post-title">{post.title}</h1>
            <img src={post.image} alt={post.title} className="mt-8 w-full aspect-[16/9] rounded-3xl object-cover shadow-lg" data-testid="blog-post-image" />
            <div className="mt-10 space-y-6">
              {post.content.map((p, i) =>
                i > 0 && p.length < 70 && !/[.!]$/.test(p) ? (
                  <h2 key={i} className="font-serif font-semibold text-2xl text-ink pt-4" data-testid={`blog-post-heading-${i}`}>{p}</h2>
                ) : (
                  <p key={i} className={`leading-relaxed ${i === 0 ? "font-serif text-xl text-ink" : "text-muted"}`} data-testid={`blog-post-paragraph-${i}`}>{p}</p>
                )
              )}
            </div>
            <ShareBar slug={post.slug} title={post.title} image={post.image} />
          </Reveal>
        )}
      </div>
    </main>
  );
}
